<template>
  <ModalComponent
    :submitButtonText="$t('portfolios.confirm_split')"
    :submit="handleSplit"
    :submitDisabled="
      !selection.hasSelectedItems ||
      !splitNewPortfolioName.trim() ||
      isProcessing
    "
    :hideSubmitButton="true"
    size="xl"
    @close="close"
    @setForceToggle="forceToggle = $event"
  >
    <template #title>
      <FontAwesomeIcon icon="fas fa-code-branch" class="text-info me-2" />
      {{ $t("portfolios.split_modal_title") }}
    </template>
    <template #body>
      <p class="text-muted mb-3">
        {{ $t("portfolios.split_select_items") }}
      </p>

      <PortfolioItemsTable
        v-if="sourcePortfolio?.items?.length"
        :selection="selection"
        :items="sourcePortfolio.items"
        :isForAdmin="true"
        :defaultVisibleColumns="PORTFOLIO_CARD_COLUMNS"
        :hideActionBar="true"
        storageContext="split-portfolio-modal"
        tableSize="compact"
      />

      <div class="mb-4">
        <label class="form-label fw-bold">{{
          $t("portfolios.split_new_name_label")
        }}</label>
        <input
          v-model="splitNewPortfolioName"
          type="text"
          class="form-control form-control-lg"
          :placeholder="$t('portfolios.split_new_name_placeholder')"
        />
      </div>
    </template>
    <template #footer>
      <div class="d-grid gap-2 w-100">
        <button
          type="button"
          class="btn btn-primary btn-lg"
          :disabled="
            !selection.hasSelectedItems ||
            !splitNewPortfolioName.trim() ||
            isProcessing
          "
          @click="handleSplit"
        >
          <span
            v-if="isProcessing"
            class="spinner-border spinner-border-sm me-2"
            role="status"
            aria-hidden="true"
          ></span>
          {{ $t("portfolios.confirm_split") }}
        </button>
        <button
          type="button"
          class="btn btn-link text-muted text-decoration-none"
          @click="close"
        >
          {{ $t("common.cancel") }}
        </button>
      </div>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import ModalComponent from "@/components/common/ModalComponent.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import PortfolioItemsTable from "@/dashboard/inventory_management/common/PortfolioItemsTable.vue";
import { allItemsSelectable } from "@/dashboard/inventory_management/common/portfolioItemsUtils";
import { PORTFOLIO_CARD_COLUMNS } from "@/dashboard/inventory_management/common/portfolioTableColumns";
import { usePortfolioSelection } from "@/dashboard/inventory_management/common/usePortfolioSelection";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import type { PortfolioGroup } from "../types";

const model = defineModel<boolean>({ required: true });

const props = defineProps<{
  sourcePortfolio: PortfolioGroup | null;
}>();

const emit = defineEmits<{
  success: [];
}>();

const { t } = useI18n();
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
const selection = usePortfolioSelection({
  policy: allItemsSelectable,
});

const forceToggle = ref<(value: boolean) => void>();
const splitNewPortfolioName = ref("");
const isProcessing = ref(false);

const close = () => {
  selection.clearSelection();
  model.value = false;
};

watch(model, (isOpen) => {
  forceToggle.value?.(isOpen);
  if (isOpen && props.sourcePortfolio) {
    selection.clearSelection();
    splitNewPortfolioName.value = t("portfolios.split_default_name", {
      name: props.sourcePortfolio.name,
      date: new Date().toLocaleDateString(),
    });
    isProcessing.value = false;
  }
});

const handleSplit = async () => {
  if (
    !props.sourcePortfolio ||
    !selection.hasSelectedItems ||
    !splitNewPortfolioName.value.trim()
  )
    return;

  isProcessing.value = true;
  try {
    const itemIds = selection.selectedItems.map((i) => i.id);
    await goSolidApi.post(`/api/portfolio/${props.sourcePortfolio.id}/split`, {
      item_ids: itemIds,
      new_portfolio_name: splitNewPortfolioName.value.trim(),
    });
    globalSuccessHandler(t("common.success"), t("common.saved_successfully"));
    selection.clearSelection();
    emit("success");
    model.value = false;
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isProcessing.value = false;
  }
};
</script>
